import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  BanIcon,
  EllipsisVerticalIcon,
  InboxIcon,
  LogOutIcon,
  PlugZapIcon,
  PlusIcon,
  PowerOffIcon,
  RefreshCwIcon,
  SmartphoneIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import { toast } from "sonner";

import { AddAccountDialog } from "@/routes/accounts/add-account-dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { AccountStatusBadge, StatusDot } from "@/components/status-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountStatus, AdminService, type Account } from "@/gen/wabms/admin/v1/admin_pb";
import { invalidateAccounts } from "@/lib/admin-queries";
import { errorMessage } from "@/lib/errors";
import {
  accountStatusHint,
  accountStatusNeedsAttention,
  accountStatusTone,
  formatCount,
  formatRelative,
} from "@/lib/format";
import { cn } from "@/lib/utils";

type LifecycleAction = "reconnect" | "disconnect" | "logout";

export function AccountsPage() {
  const queryClient = useQueryClient();
  const accountsQuery = useQuery(AdminService.method.listAccounts, {});
  const accounts = accountsQuery.data?.accounts;

  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Account | undefined>(undefined);

  const reconnect = useMutation(AdminService.method.reconnectAccount);
  const disconnect = useMutation(AdminService.method.disconnectAccount);
  const logoutAccount = useMutation(AdminService.method.logoutAccount);
  const deleteAccount = useMutation(AdminService.method.deleteAccount);

  const attention = (accounts ?? []).filter((a) => accountStatusNeedsAttention(a.status));

  async function act(kind: LifecycleAction, account: Account) {
    const mutation =
      kind === "reconnect" ? reconnect : kind === "disconnect" ? disconnect : logoutAccount;
    const p = mutation
      .mutateAsync({ accountId: account.accountId })
      .then(async (res) => {
        await invalidateAccounts(queryClient);
        return res;
      });

    toast.promise(p, {
      loading: `${kind === "reconnect" ? "Reconnecting" : kind === "disconnect" ? "Disconnecting" : "Logging out"} ${account.label}…`,
      success:
        kind === "logout"
          ? `${account.label} unlinked. It must be paired again before it can send.`
          : `${account.label} ${kind === "reconnect" ? "reconnecting" : "disconnected"}.`,
      error: (err: unknown) => errorMessage(err),
    });

    // The toast owns the failure; swallow it here so it is not an unhandled
    // rejection as well.
    await p.catch(() => undefined);
  }

  async function confirmDelete(account: Account) {
    setPendingDelete(undefined);
    const p = deleteAccount
      .mutateAsync({ accountId: account.accountId })
      .then(async (res) => {
        await invalidateAccounts(queryClient);
        return res;
      });

    toast.promise(p, {
      loading: `Deleting ${account.label}…`,
      success: `${account.label} and its tokens were deleted.`,
      error: (err: unknown) => errorMessage(err),
    });

    await p.catch(() => undefined);
  }

  return (
    <>
      <PageHeader
        title="Accounts"
        description="Each account is one linked WhatsApp companion device. Tokens, send pacing and webhooks are configured per account."
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <PlusIcon /> Add account
          </Button>
        }
      />

      {attention.length > 0 ? (
        <Alert variant="destructive" className="border-tone-danger/40 bg-tone-danger/5">
          <TriangleAlertIcon />
          <AlertTitle>
            {attention.length} account{attention.length > 1 ? "s need" : " needs"} attention
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-1 space-y-1">
              {attention.map((a) => (
                <li key={a.accountId}>
                  <span className="font-medium">{a.label}</span> —{" "}
                  {accountStatusHint(a.status)}
                  {a.queueDepth > 0 ? (
                    <>
                      {" "}
                      <span className="font-medium">
                        {formatCount(a.queueDepth)} queued message
                        {a.queueDepth === 1 ? "" : "s"}
                      </span>{" "}
                      are stuck behind it.
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {accountsQuery.isError ? (
        <ErrorState
          error={accountsQuery.error}
          title="Could not load accounts"
          onRetry={() => void accountsQuery.refetch()}
        />
      ) : accountsQuery.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-56 rounded-xl" />
          ))}
        </div>
      ) : (accounts ?? []).length === 0 ? (
        <EmptyState
          icon={<SmartphoneIcon />}
          title="No accounts yet"
          description="Add an account and pair it with a phone to start sending."
          action={
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <PlusIcon /> Add account
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(accounts ?? []).map((account) => (
            <AccountCard
              key={account.accountId}
              account={account}
              onAction={act}
              onRequestDelete={() => setPendingDelete(account)}
            />
          ))}
        </div>
      )}

      <AddAccountDialog open={addOpen} onOpenChange={setAddOpen} />

      <ConfirmDialog
        open={pendingDelete !== undefined}
        onOpenChange={(open) => !open && setPendingDelete(undefined)}
        title={`Delete ${pendingDelete?.label ?? "account"}?`}
        destructive
        confirmPhrase={pendingDelete?.label}
        confirmLabel="Delete account"
        description={
          <>
            <p>
              This removes the stored device session, every access token issued for this account,
              and its webhook configuration. Clients using those tokens start failing immediately.
            </p>
            {pendingDelete && pendingDelete.queueDepth > 0 ? (
              <p className="text-destructive">
                {formatCount(pendingDelete.queueDepth)} queued message
                {pendingDelete.queueDepth === 1 ? "" : "s"} will never be sent.
              </p>
            ) : null}
            <p>Delivery history is retained for audit; the account is not.</p>
          </>
        }
        onConfirm={() => pendingDelete && void confirmDelete(pendingDelete)}
      />
    </>
  );
}

function AccountCard({
  account,
  onAction,
  onRequestDelete,
}: {
  account: Account;
  onAction: (kind: "reconnect" | "disconnect" | "logout", account: Account) => void;
  onRequestDelete: () => void;
}) {
  const tone = accountStatusTone(account.status);
  const cap = account.sendPolicy?.dailyCap ?? 0;
  const sent = account.sentToday;
  const capPct = cap > 0 ? Math.min(100, (sent / cap) * 100) : 0;
  const paused = account.sendPolicy?.paused ?? false;
  const alarming = accountStatusNeedsAttention(account.status);

  return (
    <Card className={cn("gap-4", alarming && "border-tone-danger/50")}>
      <CardHeader className="gap-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <StatusDot tone={tone} pulse={account.status === AccountStatus.CONNECTING} />
            <Link
              to={`/accounts/${account.accountId}`}
              className="truncate font-medium hover:underline"
            >
              {account.label}
            </Link>
          </div>
          <AccountActions
            account={account}
            onAction={onAction}
            onRequestDelete={onRequestDelete}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AccountStatusBadge status={account.status} />
          {paused ? (
            <span className="rounded-full bg-tone-warning/15 px-2 py-0.5 text-[11px] font-medium text-tone-warning">
              Sending paused
            </span>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 text-sm">
        {alarming ? (
          <Alert variant="destructive" className="border-tone-danger/40 bg-tone-danger/5 py-2">
            {account.status === AccountStatus.BANNED ? <BanIcon /> : <PowerOffIcon />}
            <AlertTitle className="text-xs">
              {account.status === AccountStatus.BANNED ? "Banned by WhatsApp" : "Device unlinked"}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {accountStatusHint(account.status)}
            </AlertDescription>
          </Alert>
        ) : null}

        <dl className="space-y-1.5">
          <Row label="JID">
            {account.jid ? (
              <code className="font-mono text-xs break-all">{account.jid}</code>
            ) : (
              <span className="text-muted-foreground">not paired</span>
            )}
          </Row>
          <Row label="Push name">
            {account.pushName || <span className="text-muted-foreground">—</span>}
          </Row>
          <Row label="Last connected">
            <span className="text-muted-foreground">
              {account.lastConnectedAt ? formatRelative(account.lastConnectedAt) : "never"}
            </span>
          </Row>
        </dl>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="rounded-lg border p-2.5">
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <InboxIcon className="size-3" /> Queue depth
            </p>
            <p className="tabular mt-0.5 text-lg leading-none font-semibold">
              {formatCount(account.queueDepth)}
            </p>
          </div>
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] text-muted-foreground">Sent today</p>
            <p className="tabular mt-0.5 text-lg leading-none font-semibold">
              {formatCount(sent)}
              <span className="text-sm font-normal text-muted-foreground">
                {" / "}
                {cap > 0 ? formatCount(cap) : "∞"}
              </span>
            </p>
          </div>
        </div>

        {cap > 0 ? (
          <div className="space-y-1">
            <Progress
              value={capPct}
              className={cn(
                "h-1.5",
                capPct >= 90 && "[&>[data-slot=progress-indicator]]:bg-tone-warning",
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              {capPct >= 90
                ? "Close to the daily cap. Sending stops when it is reached."
                : `${Math.round(capPct)}% of today's cap used.`}
            </p>
          </div>
        ) : null}
      </CardContent>

      <CardFooter className="justify-between">
        <Button asChild variant="outline" size="sm">
          <Link to={`/accounts/${account.accountId}`}>Manage</Link>
        </Button>
        <span className="tabular text-[11px] text-muted-foreground">
          {account.sendPolicy?.msgsPerMinute ?? 0}/min · gap {account.sendPolicy?.minGapMs ?? 0}ms
        </span>
      </CardFooter>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] items-baseline gap-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate">{children}</dd>
    </div>
  );
}

function AccountActions({
  account,
  onAction,
  onRequestDelete,
}: {
  account: Account;
  onAction: (kind: "reconnect" | "disconnect" | "logout", account: Account) => void;
  onRequestDelete: () => void;
}) {
  const paired = account.status !== AccountStatus.UNPAIRED;
  const online =
    account.status === AccountStatus.CONNECTED || account.status === AccountStatus.CONNECTING;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${account.label}`}>
          <EllipsisVerticalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          disabled={!paired || online}
          onSelect={() => onAction("reconnect", account)}
        >
          <PlugZapIcon /> Reconnect
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!online} onSelect={() => onAction("disconnect", account)}>
          <RefreshCwIcon /> Disconnect
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!paired} onSelect={() => onAction("logout", account)}>
          <LogOutIcon /> Log out (unlink device)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onRequestDelete}>
          <Trash2Icon /> Delete account…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
